<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('mating.Male') }}</h5>
    <p class="mb-0 text-sm text-green" v-if="localFormData.maleLeased && selectedLeaseUBNs.length > 0">{{ $t('mating.Male_leased_from_UBN') }} {{ selectedLeaseUBNs.join(', ') }}</p>
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-12">
          <div class="mb-4">
            <p class="fw-bold text-sm">{{ $t('mating.Selected_male') }} ({{ selectedMales.length }})</p>
            <div class="row">
              <div v-for="male in selectedMales" :key="male.id" class="col-md-6 mb-1">
                <div class="card border  text-sm border-success bg-light" @click="toggleSelect(male)">
                  {{ displayText(male) }}
                  <span 
                    class="text-danger position-absolute top-1 end-0"
                    style="cursor: pointer; font-weight: bold; font-size: 25px; margin-right: 10px;"
                    @click.stop="toggleSelect(male)"
                  >
                    ×
                  </span>
                </div>
              </div>
              <p class="text-danger text-sm" v-if="selectedMales.length === 0" style="margin-bottom: 2px !important;">
                {{ $t('common.No_male_selected') }}
              </p>
              <p class="text-danger text-sm" v-if="v$.selectedMaleIds.$error">
                {{ v$.selectedMaleIds.$errors[0].$message }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <!-- Search Input -->
          <div class="input-group input-group-static null">
            <label class="">{{ $t('mating.Search') }}</label>
            <input v-model="searchQuery" type="text" class="form-control form-control-default" :placeholder="$t('mating.Search_UBN_studbooknumber_animalnumber_name')"> 
          </div>
        </div>
        <div class="col-12 mt-3">
          <!-- Available Males -->
          <p class="fw-bold text-sm">{{ $t('mating.Available_males') }}</p>
          <div class="row">
            <div v-for="male in filteredMales" :key="male.id" class="col-md-6 mb-1">
              <div class="card border text-sm" :class="{ 'border-success bg-light': isSelected(male.id), 'cursor-pointer': true }" @click="toggleSelect(male)">
                {{ displayText(male) }}
              </div>
            </div>
          </div>
        </div>
      </div>      
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="validateAndPrevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>                
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, ref, watch } from 'vue'
  import { helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  export default {
    name: "MatingMale",
    props: {
      males: Array,      
      formData: Object,
      prevStep: Function,
      nextStep: Function
    },
    setup(props, { emit }) {
      const { t } = useI18n()
      const localFormData = reactive(props.formData);
     
      const searchQuery = ref('')
      const selectedMaleIds = ref(
        String(props.formData.maleIds || '').split(',').filter(id => id)
      )

      const isSelected = (id) => selectedMaleIds.value.includes(id)

      const toggleSelect = (male) => {
        const index = selectedMaleIds.value.indexOf(male.id)
        if (index > -1) {
          selectedMaleIds.value.splice(index, 1)
        } else {
          selectedMaleIds.value.push(male.id)
        }
      }

      const displayText = (f) =>
        `${f.ubn} ${f.studbookNumber ? ` | ${f.studbookNumber}` : ''}${f.animalNumber ? ` | ${f.animalNumber}` : ''}${f.name ? ` (${f.name})` : ''}`

      const selectedMales = computed(() =>
        props.males.filter(f => selectedMaleIds.value.includes(f.id))
      )

      const filteredMales = computed(() =>
        props.males.filter(f =>
          !selectedMaleIds.value.includes(f.id) &&
          displayText(f).toLowerCase().includes(searchQuery.value.toLowerCase())
        )
      )

      // Vuelidate rules – require at least one selected female
      const rules = computed(() => ({
        selectedMaleIds: {
          required: helpers.withMessage(t('common.Select_at_least_one_male_animal'), value => value && value.length >= 1),
          maxLength: helpers.withMessage(t('common.Select_one_to_a_maximum_of_five_male_animals'), value => value && value.length <= 5)
        }        
      }))
      
      const v$ = useVuelidate(rules, { selectedMaleIds })
      
      const selectedLeaseUBNs = computed(() => {
        return selectedMales.value
          .filter(male => male.ubn && male.ubnId !== localFormData.ubnId)
          .map(male => male.ubn);
      });

      const maleLeasedFromUBNIds = computed(() => {
        return selectedMales.value
          .filter(male => male.ubnId && male.ubnId !== localFormData.ubnId)
          .map(male => male.ubnId);
      });

      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })

      const validateAndPrevStep = async () => {

        if (selectedMales.value.length === 0) {
          props.prevStep();
          return;
        }

        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.maleIds = selectedMaleIds.value
          localFormData.selectedLeaseUBNs = selectedLeaseUBNs.value
          localFormData.maleLeasedFromUBNIds = maleLeasedFromUBNIds.value
          props.prevStep()
        }
      }

      const validateAndNextStep = async () => {      
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          localFormData.maleIds = selectedMaleIds.value
          localFormData.selectedLeaseUBNs = selectedLeaseUBNs.value
          localFormData.maleLeasedFromUBNIds = maleLeasedFromUBNIds.value
          props.nextStep()
        }
      }

      return {
        localFormData,
        searchQuery,
        selectedMaleIds,
        selectedMales,
        filteredMales,
        isSelected,
        toggleSelect,
        displayText,
        v$,
        validateAndPrevStep,
        validateAndNextStep,
        selectedLeaseUBNs
      }
    }    
};
</script>
<style scoped>
.card {
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
  padding: 10px !important;
}
.cursor-pointer {
  cursor: pointer;
}
.bg-light {
  background-color: #f9f9f9 !important;
}
.border-success {
  border-color: #28a745 !important;
}
</style>